import { useState } from 'react';
import { Button } from '../../../../ui/common-ui/button';
import { Badge } from '../../../../ui/common-ui/badge';
import { Clock, Plus, X, Sun, Moon, Sunset, Sunrise } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../ui/common-ui/select';

interface TimeWindowSelectorProps {
  selectedWindows: string[];
  onWindowsChange: (windows: string[]) => void;
}

const hours = Array.from({ length: 24 }, (_, i) => {
  const hour = i.toString().padStart(2, '0');
  const period = i < 12 ? 'AM' : 'PM';
  const displayHour = i === 0 ? 12 : i > 12 ? i - 12 : i;
  return { value: `${hour}:00`, label: `${displayHour}:00 ${period}` };
});

const presetWindows = [
  { label: 'Business Hours', value: '09:00-17:00', icon: Sun, color: 'amber' },
  { label: 'After Hours', value: '17:00-09:00', icon: Moon, color: 'indigo' },
  { label: 'Morning', value: '06:00-12:00', icon: Sunrise, color: 'orange' },
  { label: 'Evening', value: '18:00-23:00', icon: Sunset, color: 'purple' },
];

export function TimeWindowSelector({ selectedWindows, onWindowsChange }: TimeWindowSelectorProps) {
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');

  const addCustomWindow = () => {
    const window = `${startTime}-${endTime}`;
    if (!selectedWindows.includes(window)) {
      onWindowsChange([...selectedWindows, window]);
    }
  };

  const addPresetWindow = (value: string) => {
    if (!selectedWindows.includes(value)) {
      onWindowsChange([...selectedWindows, value]);
    }
  };

  const removeWindow = (window: string) => {
    onWindowsChange(selectedWindows.filter(w => w !== window));
  };

  const formatTimeDisplay = (window: string) => {
    const [start, end] = window.split('-');
    const formatTime = (time: string) => {
      const [hour] = time.split(':');
      const h = parseInt(hour);
      const period = h < 12 ? 'AM' : 'PM';
      const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
      return `${displayHour}:00 ${period}`;
    };
    return `${formatTime(start)} - ${formatTime(end)}`;
  };

  return (
    <div className="space-y-4">
      {/* Selected Time Windows - Moved to top */}
      {selectedWindows.length > 0 && (
        <div className="space-y-2">
          <label className="text-xs font-medium text-muted-foreground">Selected Windows</label>
          <div className="flex flex-wrap gap-2">
            {selectedWindows.map((window) => (
              <Badge
                key={window}
                variant="secondary"
                className="gap-1.5 pr-1 pl-2 py-1 bg-primary/10 text-primary border border-primary/20"
              >
                <Clock className="h-3 w-3" />
                {formatTimeDisplay(window)}
                <button
                  onClick={() => removeWindow(window)}
                  className="ml-1 p-0.5 rounded-full hover:bg-primary/20 transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Preset Quick Selections */}
      <div className="space-y-2">
        <label className="text-xs font-medium text-muted-foreground">Quick Presets</label>
        <div className="grid grid-cols-2 gap-2">
          {presetWindows.map((preset) => {
            const Icon = preset.icon;
            const isSelected = selectedWindows.includes(preset.value);
            return (
              <button
                key={preset.value}
                onClick={() => isSelected ? removeWindow(preset.value) : addPresetWindow(preset.value)}
                className={`
                  flex items-center gap-2 p-3 rounded-lg border transition-all duration-200
                  ${isSelected 
                    ? 'bg-primary/10 border-primary/50 text-primary' 
                    : 'bg-muted/30 border-border hover:border-primary/30 hover:bg-muted/50'}
                `}
              >
                <Icon className="h-4 w-4" />
                <div className="text-left flex-1">
                  <p className="text-xs font-medium">{preset.label}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {formatTimeDisplay(preset.value)}
                  </p>
                </div>
                {isSelected && (
                  <div className="w-2 h-2 rounded-full bg-primary" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Custom Time Range */}
      <div className="space-y-2">
        <label className="text-xs font-medium text-muted-foreground">Custom Time Range</label>
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <Select value={startTime} onValueChange={setStartTime}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Start" />
              </SelectTrigger>
              <SelectContent>
                {hours.map((hour) => (
                  <SelectItem key={hour.value} value={hour.value}>
                    {hour.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <span className="text-muted-foreground text-sm">to</span>
          <div className="flex-1">
            <Select value={endTime} onValueChange={setEndTime}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="End" />
              </SelectTrigger>
              <SelectContent>
                {hours.map((hour) => (
                  <SelectItem key={hour.value} value={hour.value}>
                    {hour.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={addCustomWindow}
            size="sm"
            variant="outline"
            className="h-9 gap-1"
          >
            <Plus className="h-3 w-3" />
            Add
          </Button>
        </div>
      </div>

    </div>
  );
}